import { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Button,
  InputLabel,
  Select,
  IconButton,
  CircularProgress,
  DialogActions,
  useMediaQuery,
} from '@mui/material';
import { UploadFile, Close } from '@mui/icons-material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { errorTost, successTost } from '../../Services/Toast';
import {
  HELPDESK_BASE_URL,
  HELPDESK_TENANT_ID,
  HELPDESK_TYPE_ID,
  CRM_DEPARTMENTS,
  CRM_DEPARTMENT,
  buildHelpDeskUser,
} from './helpDeskConfig';

const PRIMARY = '#1570EF';
const PRIMARY_HOVER = '#0E5BC4';
const MAX_FILES = 10;

const TicketForm = ({ onClose }) => {
  const loggedInUser = useSelector((state) => state.Auth.user);
  const hdUser = buildHelpDeskUser(loggedInUser);
  const isMobile = useMediaQuery('(max-width: 600px)');

  // Pinned list -- no lookup, so no state needed. Add entries in helpDeskConfig.
  const department = CRM_DEPARTMENTS;
  const [categoryLevel, setCategoryLevel] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [subcategoryLevel, setSubcategoryLevel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const [form, setForm] = useState({
    department: CRM_DEPARTMENT.name,
    department_id: CRM_DEPARTMENT.id,
    category: '',
    category_id: '',
    subcategory: '',
    subcategory_id: '',
    priority: 'Low',
    description: '',
    subject_line: '',
    file: null,
  });

  const priorities = [
    { name: 'Low', id: 1 },
    { name: 'Medium', id: 2 },
    { name: 'High', id: 3 },
  ];

  // ---------------- API ----------------
  const getLevelOneCategories = async (departmentId) => {
    try {
      setCategoryLoading(true);
      // Drop the previous department's categories first, so a failed fetch can
      // never leave a stale list that belongs to a different department.
      setCategoryLevel([]);
      const { data } = await axios.get(
        `${HELPDESK_BASE_URL}/api/user/getAllLevelOneCategories/${departmentId}`
      );
      if (data.success) {
        setCategoryLevel(data.categories);
      } else {
        console.error('Category error:', data?.message);
        errorTost('Could not load categories for this department');
      }
    } catch (err) {
      console.error('Category error:', err);
      errorTost('Could not load categories for this department');
    } finally {
      setCategoryLoading(false);
    }
  };

  const getSubcategories = async (departmentId, categoryId) => {
    try {
      // Drop the previous category's subcategories first, so a failed fetch can
      // never leave a stale list that belongs to a different category.
      setSubcategoryLevel([]);
      const { data } = await axios.get(
        `${HELPDESK_BASE_URL}/api/user/get-subcategories`,
        { params: { department_id: departmentId, parent_id: categoryId } }
      );
      if (data.success) {
        setSubcategoryLevel(data.subcategories || []);
      } else {
        console.error('Subcategory error:', data?.message);
        errorTost('Could not load subcategories for this category');
      }
    } catch (err) {
      console.error('Subcategory error:', err);
      errorTost('Could not load subcategories for this category');
    }
  };

  useEffect(() => {
    if (form.department_id) getLevelOneCategories(form.department_id);
  }, [form.department_id]);

  // Subcategories hang off the chosen category. Changing department clears
  // category_id, which must empty this list too -- hence the else branch.
  useEffect(() => {
    if (form.department_id && form.category_id) {
      getSubcategories(form.department_id, form.category_id);
    } else {
      setSubcategoryLevel([]);
    }
  }, [form.department_id, form.category_id]);

  // ---------------- HANDLERS ----------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (event, optionsList, keyName) => {
    const selectedValue = event.target.value;
    const selectedOption = optionsList.find((opt) => opt.name === selectedValue);

    if (selectedOption) {
      setForm((prev) => ({
        ...prev,
        [keyName]: selectedOption.name,
        [`${keyName}_id`]: selectedOption.id,
        ...(keyName === 'department' && {
          category: '',
          category_id: '',
          subcategory: '',
          subcategory_id: '',
        }),
        ...(keyName === 'category' && {
          subcategory: '',
          subcategory_id: '',
          priority:
            selectedOption.name === 'Leakage and seepage' ? 'High' : 'Medium',
        }),
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [keyName]: '',
        [`${keyName}_id`]: '',
        ...(keyName === 'category' && { subcategory: '', subcategory_id: '' }),
      }));
    }
  };

  const handleFileChange = (e) => {
    const newFiles = e.target.files;
    setForm((prev) => {
      const currentFiles = Array.isArray(prev.file) ? prev.file : [];
      if (currentFiles.length + newFiles.length > MAX_FILES) {
        errorTost(`You can upload a maximum of ${MAX_FILES} images.`);
        return prev;
      }
      const updatedFiles = [
        ...currentFiles,
        ...Array.from(newFiles).filter(
          (file) =>
            !currentFiles.some((existingFile) => existingFile.name === file.name)
        ),
      ];
      return { ...prev, file: updatedFiles };
    });
  };

  const handleFileRemove = (index) => {
    setForm((prev) => ({
      ...prev,
      file: prev.file.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    if (!form.department) return errorTost('Department is required'), false;
    if (!form.category) return errorTost('Category is required'), false;
    if (subcategoryLevel.length > 0 && !form.subcategory)
      return errorTost('Subcategory is required'), false;
    if (!form.priority) return errorTost('Priority is required'), false;
    if (!form.subject_line) return errorTost('Subject is required'), false;
    return true;
  };

  const submitTicket = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setIsDisabled(true);
    try {
      const formData = new FormData();
      formData.append('department_id', form.department_id);
      formData.append('category_id', form.category_id);
      formData.append('sub_category_id', form.subcategory_id);
      formData.append('priority_level', form.priority);
      formData.append('description', form.description);
      formData.append('name', hdUser.name);
      formData.append('email', hdUser.email);
      formData.append('phone', hdUser.mobile);
      formData.append('department_name', form.department);
      formData.append('category_name', form.category);
      formData.append('subcategory_name', form.subcategory);
      formData.append('customer_id', hdUser.crmUserId);
      formData.append('subject_line', form.subject_line);
      formData.append('tenant_id', HELPDESK_TENANT_ID);
      // CRM: ticket owner is the logged-in CRM user
      formData.append('other_id', hdUser.crmUserId || '');
      // Fixed type_id identifying CRM-sourced tickets
      formData.append('type_id', HELPDESK_TYPE_ID);


      if (form.file && form.file.length > 0) {
        form.file.forEach((file) => {
          formData.append('attachments', file);
        });
      }

      const res = await axios.post(
        `${HELPDESK_BASE_URL}/api/user/create-ticket`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (res.data.success) {
        successTost('Ticket submitted successfully!');
        onClose(true);
      } else if (
        res.data.success === false &&
        res.data.message === 'No users found in department'
      ) {
        console.error(res.data.message);
        errorTost('No users found in department');
      } else {
        errorTost('Failed to submit ticket.');
      }
    } catch (err) {
      errorTost('Failed to submit ticket.');
      console.error(err);
    } finally {
      setLoading(false);
      setIsDisabled(false);
    }
  };

  const fieldSx = {
    height: '45px',
    fontFamily: 'Roboto-Regular',
    '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccc' },
    '&:hover .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccc' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccc' },
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'Roboto-Regular',
      '& fieldset': { borderColor: '#ccc' },
      '&:hover fieldset': { borderColor: '#ccc' },
      '&.Mui-focused fieldset': { borderColor: '#ccc' },
    },
  };

  const labelSx = { mb: 1, fontFamily: 'Roboto-Regular', color: 'black' };

  return (
    <Box sx={{ p: 3, maxWidth: '100%', overflow: 'auto' }}>
      <Grid container spacing={2}>
        {/* Department (CRM only) */}
        <Grid item xs={12} sm={6}>
          <InputLabel sx={labelSx}>
            Department <span style={{ color: 'red' }}>*</span>
          </InputLabel>
          <Select
            sx={fieldSx}
            name="department"
            fullWidth
            displayEmpty
            value={form.department}
            onChange={(e) => handleDropdownChange(e, department, 'department')}
            renderValue={(selected) => {
              if (!selected) return <em>-- Select --</em>;
              return selected;
            }}>
            {department.map((d) => (
              <MenuItem key={d.id} value={d.name}>
                {d.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        {/* Category */}
        <Grid item xs={12} sm={6}>
          <InputLabel sx={labelSx}>
            Category <span style={{ color: 'red' }}>*</span>
          </InputLabel>
          <Select
            sx={fieldSx}
            name="category"
            value={form.category}
            onChange={(e) => handleDropdownChange(e, categoryLevel, 'category')}
            displayEmpty
            fullWidth>
            <MenuItem value="">
              <em>-- Select --</em>
            </MenuItem>
            {categoryLoading ? (
              <MenuItem disabled>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography sx={{ fontFamily: 'Roboto-Regular', fontSize: 14 }}>
                    Loading categories...
                  </Typography>
                </Box>
              </MenuItem>
            ) : (
              categoryLevel.map((cat) => (
                <MenuItem key={cat.id} value={cat.name}>
                  {cat.name}
                </MenuItem>
              ))
            )}
          </Select>
        </Grid>

        {/* Subcategory -- rendered only when the chosen category has any */}
        {subcategoryLevel.length > 0 && (
          <Grid item xs={12} sm={6}>
            <InputLabel sx={labelSx}>
              Subcategory <span style={{ color: 'red' }}>*</span>
            </InputLabel>
            <Select
              sx={fieldSx}
              name="subcategory"
              value={form.subcategory}
              onChange={(e) =>
                handleDropdownChange(e, subcategoryLevel, 'subcategory')
              }
              displayEmpty
              fullWidth>
              <MenuItem value="">
                <em>-- Select --</em>
              </MenuItem>
              {subcategoryLevel.map((sub) => (
                <MenuItem key={sub.id} value={sub.name}>
                  {sub.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}

        {/* Priority */}
        <Grid item xs={12} sm={6}>
          <InputLabel sx={labelSx}>
            Priority <span style={{ color: 'red' }}>*</span>
          </InputLabel>
          <Select
            sx={fieldSx}
            fullWidth
            displayEmpty
            name="priority"
            value={form.priority}
            onChange={(e) => handleDropdownChange(e, priorities, 'priority')}>
            <MenuItem value="">
              <em>-- Select --</em>
            </MenuItem>
            {priorities.map((priority) => (
              <MenuItem key={priority.id} value={priority.name}>
                {priority.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        {/* Subject */}
        <Grid item xs={12}>
          <InputLabel sx={labelSx}>
            Subject <span style={{ color: 'red' }}>*</span>
          </InputLabel>
          <TextField
            inputProps={{ maxLength: 50 }}
            sx={textFieldSx}
            placeholder="E.g. Unable to update bank details"
            fullWidth
            name="subject_line"
            value={form.subject_line || ''}
            onChange={handleChange}
          />
          <label style={{ fontFamily: 'Roboto-Regular', fontSize: '12px', color: '#666' }}>
            {form.subject_line ? form.subject_line.length : 0}/50 characters
          </label>
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <InputLabel sx={labelSx}>Description</InputLabel>
          <TextField
            placeholder="Describe your issue here..."
            multiline
            rows={3}
            fullWidth
            inputProps={{ maxLength: 500 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '100px',
                fontFamily: 'Roboto-Regular',
                '& fieldset': { borderColor: '#ccc' },
                '&:hover fieldset': { borderColor: '#ccc' },
                '&.Mui-focused fieldset': { borderColor: '#ccc' },
              },
            }}
            name="description"
            value={form.description}
            onChange={handleChange}
          />
          <label style={{ fontFamily: 'Roboto-Regular', fontSize: '12px', color: '#666' }}>
            {form.description ? form.description.length : 0}/500 characters
          </label>
        </Grid>

        {/* Attachments */}
        <Grid item xs={12}>
          <InputLabel sx={labelSx}>Attachments (Optional)</InputLabel>
          <Box sx={{ mt: '20px', width: { xs: '63%', md: '30%' }, display: 'flex' }}>
            <Button
              variant="contained"
              component="label"
              sx={{
                textTransform: 'none',
                padding: '12px 24px',
                fontSize: '16px',
                fontFamily: 'Roboto-Regular',
                borderRadius: '8px',
                backgroundColor: PRIMARY,
                '&:hover': { backgroundColor: PRIMARY_HOVER },
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
              <UploadFile sx={{ fontSize: { xs: 30, md: 24 } }} />
              <Typography sx={{ fontSize: { xs: 16, md: 16 }, fontWeight: 'bold' }}>
                Choose Files
              </Typography>
              <input
                id="file-upload"
                type="file"
                multiple
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png"
                style={{ display: 'none' }}
              />
            </Button>
          </Box>

          {form.file && form.file.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3, mt: 3 }}>
              {form.file.map((file, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#edededff',
                    padding: '8px',
                    borderRadius: '8px',
                    width: 'fit-content',
                  }}>
                  <Typography sx={{ fontFamily: 'Roboto-Regular', color: 'black' }}>
                    {file.name}
                  </Typography>
                  <IconButton sx={{ color: '#F44336' }} onClick={() => handleFileRemove(idx)}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <DialogActions>
            <Button
              onClick={() => onClose(false)}
              sx={{
                float: 'right',
                background: '#fff',
                border: `1px solid ${PRIMARY}`,
                textTransform: 'none',
                '&:hover': { background: '#EFF8FF' },
                color: PRIMARY,
                fontSize: '16px',
                fontFamily: 'Roboto-Regular',
                width: isMobile ? '100%' : '200px',
              }}>
              Cancel
            </Button>
            <Button
              sx={{
                float: 'right',
                background: isDisabled ? '#ccc' : PRIMARY,
                textTransform: 'none',
                '&:hover': { background: PRIMARY_HOVER },
                color: '#fff',
                fontSize: '16px',
                fontFamily: 'Roboto-Regular',
                width: isMobile ? '100%' : '200px',
              }}
              disabled={isDisabled}
              onClick={submitTicket}>
              {loading ? (
                <CircularProgress size={24} sx={{ color: '#fff' }} />
              ) : isMobile ? (
                'Submit'
              ) : (
                'Submit Ticket'
              )}
            </Button>
          </DialogActions>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TicketForm;
